
if (!customElements.get('bwfs-drag-scroll')) {
  customElements.define('bwfs-drag-scroll', class extends HTMLElement {
    connectedCallback() {
      if (this.dragController) return;
      this.dragController = new AbortController();
      const options = {signal:this.dragController.signal};
      this.addEventListener('pointerdown', event => this.startDrag(event), options);
      window.addEventListener('pointermove', event => this.moveDrag(event), {...options,passive:false});
      window.addEventListener('pointerup', event => this.endDrag(event), options);
      window.addEventListener('pointercancel', event => this.endDrag(event,true), options);
      window.addEventListener('blur', () => this.endDrag(null,true), options);
      this.addEventListener('lostpointercapture', event => this.endDrag(event,true), options);
      this.addEventListener('dragstart', event => {
        if (this.pointer) event.preventDefault();
      }, options);
      this.addEventListener('click', event => {
        if (this.suppressDragClick && event.detail > 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.suppressDragClick = false;
        }
      }, {...options,capture:true});
      this.dragResize = new ResizeObserver(() => this.toggleAttribute('data-draggable',this.scrollWidth > this.clientWidth + 2));
      this.dragResize.observe(this);
    }
    startDrag(event) {
      clearTimeout(this.clickReset);
      this.suppressDragClick = false;
      if (event.pointerType !== 'mouse' || event.button !== 0 || !event.isPrimary || this.scrollWidth <= this.clientWidth + 2) return;
      if (event.target.closest('input,textarea,select,video,iframe,model-viewer,[data-no-drag]')) return;
      this.scrollTo({left:this.scrollLeft,behavior:'instant'});
      this.pointer = {id:event.pointerId,x:event.clientX,y:event.clientY,left:this.scrollLeft,delta:0,dragged:false,snap:getComputedStyle(this).scrollSnapType !== 'none'};
      this.setAttribute('data-pointer-down','');
    }
    moveDrag(event) {
      const pointer = this.pointer;
      if (!pointer || event.pointerId !== pointer.id) return;
      const delta = event.clientX - pointer.x;
      if (!pointer.dragged) {
        if (Math.abs(delta) < 6) return;
        if (Math.abs(event.clientY-pointer.y) > Math.abs(delta)) {this.endDrag(event,true);return;}
        pointer.dragged = true;
        this.classList.add('is-dragging');
        this.setPointerCapture(pointer.id);
      }
      event.preventDefault();
      pointer.delta = delta;
      this.scrollLeft = pointer.left - delta;
    }
    snapDestination(pointer,cancelled) {
      const max = Math.max(0,this.scrollWidth-this.clientWidth);
      const rect = this.getBoundingClientRect();
      const padding = parseFloat(getComputedStyle(this).scrollPaddingLeft) || 0;
      const stops = [...new Set([0,...[...this.children].map(child => Math.max(0,Math.min(max,child.getBoundingClientRect().left-rect.left+this.scrollLeft-padding))),max])].sort((a,b)=>a-b);
      const nearest = value => stops.reduce((best,stop)=>Math.abs(stop-value)<Math.abs(best-value)?stop:best,stops[0]);
      const start = nearest(pointer.left);
      let target = nearest(this.scrollLeft);
      if (!cancelled && Math.abs(target-start)<1) {
        const index = stops.indexOf(start);
        const next = stops[index+(pointer.delta<0?1:-1)];
        if (next !== undefined && Math.abs(pointer.delta)>Math.min(100,Math.abs(next-start)*.2)) target=next;
      }
      return target;
    }
    endDrag(event,cancelled=false) {
      const pointer = this.pointer;
      if (!pointer || (event && event.pointerId !== pointer.id)) return;
      this.pointer = null;
      const target = pointer.dragged && pointer.snap ? this.snapDestination(pointer,cancelled) : null;
      this.classList.remove('is-dragging');
      this.removeAttribute('data-pointer-down');
      if (this.hasPointerCapture(pointer.id)) this.releasePointerCapture(pointer.id);
      if (pointer.dragged) {
        this.suppressDragClick = true;
        this.clickReset = setTimeout(()=>{this.suppressDragClick=false;},400);
        if (target !== null) this.scrollTo({left:target,behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
      }
    }
    disconnectedCallback() {
      this.endDrag(null,true);
      this.dragController?.abort();
      this.dragController = null;
      this.dragResize?.disconnect();
      clearTimeout(this.clickReset);
      this.suppressDragClick = false;
    }
  });
}


if (!customElements.get('bwfs-reviews')) customElements.define('bwfs-reviews', class extends HTMLElement {
  connectedCallback() {
    if (this.classList.contains('bwfr--comments')) return;
    if (this.controller) return;
    this.controller = new AbortController();
    const options = {signal:this.controller.signal};
    this.track = this.querySelector('.bwfr-track');
    this.cards = [...this.querySelectorAll('.bwfr-card')];
    this.photos = this.cards.filter(card=>card.querySelector('.bwfr-photo'));
    this.dialog = this.querySelector('dialog');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.modalIndex = 0;
    this.querySelectorAll('[data-helpful]').forEach(button=>button.addEventListener('click',()=>button.setAttribute('aria-pressed',String(button.getAttribute('aria-pressed')!=='true')),options));
    this.querySelectorAll('[data-share-url]').forEach(button=>button.addEventListener('click',async()=>{
      const status=button.closest('.bwfr-card').querySelector('.bwfr-social-status');
      try{await navigator.clipboard.writeText(button.dataset.shareUrl);status.textContent=button.dataset.copySuccess;}
      catch{status.textContent=button.dataset.copyFailed;}
    },options));
    this.prev.addEventListener('click', () => this.move(-1), options);
    this.next.addEventListener('click', () => this.move(1), options);
    this.track.addEventListener('scroll', () => {cancelAnimationFrame(this.frame);this.frame=requestAnimationFrame(()=>this.update());}, {...options,passive:true});
    this.track.addEventListener('keydown', event => {
      if (event.target !== this.track) return;
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {event.preventDefault();this.move(event.key === 'ArrowRight' ? 1 : -1);}
      if (event.key === 'Home' || event.key === 'End') {event.preventDefault();this.track.scrollTo({left:event.key==='Home'?0:this.track.scrollWidth,behavior:this.behavior()});}
    }, options);
    this.photos.forEach((card,index)=>card.querySelector('.bwfr-photo').addEventListener('click',event=>{
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();this.opener=event.currentTarget;this.showPhoto(index);this.dialog.showModal();this.querySelector('[data-close]').focus();
    }, options));
    this.querySelector('[data-close]').addEventListener('click',()=>this.dialog.close(),options);
    this.querySelector('[data-modal-prev]').addEventListener('click',()=>this.showPhoto(this.modalIndex-1),options);
    this.querySelector('[data-modal-next]').addEventListener('click',()=>this.showPhoto(this.modalIndex+1),options);
    this.dialog.addEventListener('click',event=>{if(event.target===this.dialog){const r=this.dialog.getBoundingClientRect();if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)this.dialog.close();}},options);
    this.dialog.addEventListener('keydown',event=>{if(event.key==='ArrowRight'||event.key==='ArrowLeft'){event.preventDefault();this.showPhoto(this.modalIndex+(event.key==='ArrowRight'?1:-1));}},options);
    this.dialog.addEventListener('close',()=>this.opener?.focus({preventScroll:true}),options);
    this.resizeObserver=new ResizeObserver(()=>this.update());this.resizeObserver.observe(this.track);
    this.setAttribute('data-ready','');this.update();
  }
  behavior(){return matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth';}
  move(direction){const step=this.cards.length>1?this.cards[1].offsetLeft-this.cards[0].offsetLeft:this.track.clientWidth;this.track.scrollBy({left:direction*step,behavior:this.behavior()});}
  update(){
    const max=this.track.scrollWidth-this.track.clientWidth;
    this.style.setProperty('--bwfr-progress',Math.min(100,(this.track.scrollLeft+this.track.clientWidth)/this.track.scrollWidth*100)+'%');
    this.prev.disabled=this.track.scrollLeft<=2;this.next.disabled=this.track.scrollLeft>=max-2;
    const bounds=this.track.getBoundingClientRect();
    const visible=this.cards.map((card,index)=>({index,rect:card.getBoundingClientRect()})).filter(({rect})=>Math.min(rect.right,bounds.right)-Math.max(rect.left,bounds.left)>rect.width*.5);
    if(visible.length){const start=visible[0].index+1,end=visible.at(-1).index+1;this.querySelector('.bwfr-position').textContent=(start===end?start:`${start}–${end}`)+' / '+this.cards.length;}
  }
  showPhoto(index){
    this.modalIndex=Math.max(0,Math.min(this.photos.length-1,index));
    const card=this.photos[this.modalIndex],img=card.querySelector('img').cloneNode(true),copy=document.createElement('div');
    img.loading='eager';copy.className='bwfr-dialog-copy';copy.append(card.querySelector('.bwfr-person').cloneNode(true),card.querySelector('.bwfr-stars').cloneNode(true),card.querySelector('blockquote').cloneNode(true));
    this.querySelector('.bwfr-dialog-content').replaceChildren(img,copy);
    this.querySelector('.bwfr-modal-position').textContent=(this.modalIndex+1)+' / '+this.photos.length;
    this.querySelector('[data-modal-prev]').disabled=this.modalIndex===0;this.querySelector('[data-modal-next]').disabled=this.modalIndex===this.photos.length-1;
  }
  disconnectedCallback(){this.controller?.abort();this.resizeObserver?.disconnect();cancelAnimationFrame(this.frame);this.controller=null;}
});
